import { useCallback, useContext, useEffect, useState } from 'react'
import { Controller, FieldValues, useForm } from 'react-hook-form'
import { SelectItem, getSelectItemOptionLabel, normalizeSelectItems, isSelectItemEqual } from '../../../types/SelectItem'
import { DateSearch } from '../../SearchBar/DateSearch'
import {
  Autocomplete,
  AutocompleteInputChangeReason,
  FormControl,
  FormControlLabel,
  FormLabel,
  Grid,
  Radio,
  RadioGroup,
  TextField,
  createFilterOptions,
} from '@mui/material'
import { ListViewContext } from '../../../contexts/ListViewContext'
import { FilterControls } from './FilterControls'
import {
  useAutocompleteOriginalTitleOptionsLazyQuery,
  useOriginTitlesCreateOptionsQuery,
} from '@kistill/client/data-access'
import {
  handleOnSubmit,
  OnFilterType,
} from '../../../hooks/useHandleOnSubmitFilters'
import { getHiddenFilterFields } from '../filterConfig'
import { useClearFiltersOnColumnHide } from '../../../hooks/useClearFiltersOnColumnHide'
import { useFilterSync } from '../../../hooks/useFilterSync'

interface OriginalTitleFilterProps {
  onFilter: OnFilterType
  selected: readonly string[]
  setSelected: (selected: Set<string>) => void
  columnVisibility?: { [key: string]: boolean }
  initialFilterParams?: Record<string, unknown>
}

export function OriginalTitleFilters({
  onFilter,
  selected,
  setSelected,
  columnVisibility = {},
  initialFilterParams,
}: OriginalTitleFilterProps) {
  // Get hidden filter fields based on column visibility
  const hiddenFields = getHiddenFilterFields('originalTitle', columnVisibility)
  const { handleSubmit, watch, control, register, setValue, reset, getValues } =
    useForm({ shouldUnregister: true })
  const { showOnlySelected, updateShowOnlySelected } =
    useContext(ListViewContext)
  const [inputValue, setInputValue] = useState('')
  const [options, setOptions] = useState<SelectItem[]>([])

  const [getOriginalTitleOptions] =
    useAutocompleteOriginalTitleOptionsLazyQuery({
      variables: { query: inputValue },
      fetchPolicy: 'cache-and-network',
      onCompleted: (data) => {
        if (data.originalTitleOptions) {
          setOptions(
            data.originalTitleOptions.map((x) => {
              return { id: x?.id, title: x.title }
            }),
          )
        }
      },
    })
  const { data } = useOriginTitlesCreateOptionsQuery({ fetchPolicy: 'network-only' })
  const countryOfOriginOptions = data?.countryOfOriginOptions as SelectItem[]
  const overClassificationOptions =
    data?.overClassificationOptions as SelectItem[]
  const subClassificationOptions = data?.subClassificationOptions
    ? (data.subClassificationOptions as SelectItem[]).filter(
        (item, index, self) => self.findIndex((x) => x.id === item.id) === index,
      )
    : undefined
  const sources = data?.sourceOptions as SelectItem[]

  const onSubmit = useCallback(
    (data: FieldValues) => {
      handleOnSubmit(onFilter)(data)
    },
    [onFilter],
  )

  const { formStateRef, clearFilters, clearSpecificFields } = useFilterSync({
    initialFilterParams,
    reset,
    watch,
    onSubmit,
    getSyncKey: (normalized) =>
      normalized === null ? null : JSON.stringify(normalized),
    getClearSyncKey: () => 'originalTitle',
  })

  const handleInputChange = (
    // eslint-disable-next-line @typescript-eslint/ban-types
    _event: React.ChangeEvent<{}>,
    value: string,
    _reason: AutocompleteInputChangeReason,
  ) => {
    setInputValue(value)
  }

  const filterOptions = createFilterOptions({
    trim: true,
  })

  const deselectCheckboxChange = () => {
    setSelected(new Set())
    if (showOnlySelected) {
      setValue('selectedGuids', null)
      updateShowOnlySelected(!showOnlySelected)
      reset({})
    }
  }

  useEffect(() => {
    getOriginalTitleOptions()
  }, [getOriginalTitleOptions])

  // Clear filter values when their corresponding columns are hidden
  useClearFiltersOnColumnHide(
    'originalTitle',
    columnVisibility,
    setValue,
    clearSpecificFields,
  )

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Grid container spacing={1}>
        {!hiddenFields.has('originalTitleSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='originalTitleSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(options || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, originalTitleSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  onInputChange={handleInputChange}
                  value={value || []}
                  clearOnBlur={false}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label='Frumtitill' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('althjodlegurTitill') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='althjodlegurTitill'
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Alþjóðlegur titill'
                  fullWidth
                  size='small'
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('countriesSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='countriesSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(countryOfOriginOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, countriesSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  filterOptions={filterOptions}
                  renderOption={(props, option) => {
                    return (
                      <li {...props} key={(option as SelectItem).id}>
                        {getSelectItemOptionLabel(option)}
                      </li>
                    )
                  }}
                  renderInput={(params) => (
                    <TextField {...params} label='Upprunalönd' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('overClassificationSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='overClassificationSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(overClassificationOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, overClassificationSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  renderInput={(params) => (
                    <TextField {...params} label='Yfirflokkar' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('subClassificationSelectItem') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='subClassificationSelectItem'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(subClassificationOptions || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, subClassificationSelectItem: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  renderInput={(params) => (
                    <TextField {...params} label='Undirflokkar' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('source') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='source'
              render={({ field: { onChange, value } }) => (
                <Autocomplete
                  multiple
                  size='small'
                  options={normalizeSelectItems(sources || [])}
                  getOptionLabel={getSelectItemOptionLabel}
                  isOptionEqualToValue={isSelectItemEqual}
                  onChange={(e, data) => {
                    const next = normalizeSelectItems(data)
                    onChange(next)
                    formStateRef.current = { ...formStateRef.current, source: next }
                    setTimeout(() => handleOnSubmit(onFilter)(formStateRef.current), 0)
                  }}
                  value={value || []}
                  renderInput={(params) => (
                    <TextField {...params} label='Heimild' />
                  )}
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('locationInHouse') && (
          <Grid item xs={12} md={2} sx={{ pr: 0.25 }}>
            <Controller
              control={control}
              name='locationInHouse'
              render={({ field }) => (
                <TextField
                  {...field}
                  label='Staðsetning frumefnis utanhúss'
                  fullWidth
                  size='small'
                />
              )}
            />
          </Grid>
        )}
        {!hiddenFields.has('dateFrom') && !hiddenFields.has('dateTo') && (
          <Grid item xs={12} md={4} sx={{ pr: 0.25 }}>
            <DateSearch
              register={register}
              control={control}
              dateFromLabel={'Ár frá'}
              dateToLabel={'Ár til'}
            />
          </Grid>
        )}
        {!hiddenFields.has('originalTitleType') && (
          <Grid item xs={12} md='auto' sx={{ pr: 0.25 }}>
            <FormControl sx={{ padding: 0 }}>
              <FormLabel sx={{ marginBottom: 0.5 }}>Tegund frumtitils</FormLabel>
              <RadioGroup
                row
                sx={{ margin: 0, gap: 0 }}
                value={getValues('originalTitleType') || ''}
                onChange={(event) =>
                  setValue('originalTitleType', event.target.value)
                }
              >
                <FormControlLabel
                  value={''}
                  control={
                    <Radio
                      checked={getValues('originalTitleType') === ''}
                      size='small'
                    />
                  }
                  label='Allir'
                  sx={{ marginRight: 1, marginLeft: 0 }}
                />
                <FormControlLabel
                  value='1'
                  control={<Radio size='small' />}
                  label='Útgáfutitill'
                  sx={{ marginRight: 1, marginLeft: 0 }}
                />
                <FormControlLabel
                  value='2'
                  control={<Radio size='small' />}
                  label='Safntitill'
                  sx={{ marginRight: 0, marginLeft: 0 }}
                />
              </RadioGroup>
            </FormControl>
          </Grid>
        )}
      </Grid>
      <FilterControls
        selected={selected}
        clearFilters={clearFilters}
        register={register}
        deselectCheckboxChange={deselectCheckboxChange}
      />
    </form>
  )
}
